import React, { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { useApi } from '../../contexts/ApiContext';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import { PLANS } from '../../config/plans';
import './FileUploader.css';

interface FileUploaderProps {
  folderId?: string;
  onUploadSuccess?: (document: any) => void;
  onUploadError?: (error: string) => void;
  maxSize?: number;
  showProgress?: boolean;
  compact?: boolean;
  className?: string;
}

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  folderId,
  onUploadSuccess,
  onUploadError,
  maxSize,
  showProgress = true,
  compact = false,
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState('');

  const { documents } = useApi();
  const { user, planLimits } = useAuth();

  // Keep a ref so uploadFile always reads the latest folderId
  // regardless of when the useCallback was last recreated
  const folderIdRef = useRef(folderId);
  useEffect(() => { folderIdRef.current = folderId; }, [folderId]);

  const getMaxFileSize = () => {
    if (maxSize !== undefined) return maxSize;
    const limitMb = user ? planLimits.maxAuthFileSizeMb : PLANS.free.maxAnonymousFileSizeMb;
    return limitMb * 1024 * 1024;
  };

  const ACCEPTED_TYPES = new Set(['application/pdf']);

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_TYPES.has(file.type)) {
      return 'File type not supported. Please upload a PDF file.';
    }

    // Check file size
    const maxFileSize = getMaxFileSize();
    if (maxFileSize && file.size > maxFileSize) {
      const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
      return `File size must be under ${maxSizeMB}MB${!user ? ' for anonymous uploads' : ''}`;
    }

    return null;
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    setError('');
    setUploadProgress({ loaded: 0, total: file.size, percentage: 0 });

    try {
      // Step 1: get presigned S3 URL (validates plan limits server-side)
      const { upload_url, document_id } = user
        ? await documents.requestUploadUrl(file, folderIdRef.current)
        : await documents.requestAnonymousUploadUrl(file);

      // Step 2: PUT directly to S3 — bypasses API Gateway, real progress events
      await axios.put(upload_url, file, {
        headers: { 'Content-Type': 'application/pdf' },
        onUploadProgress: (e) => {
          const loaded = e.loaded;
          const total = e.total ?? file.size;
          // Cap at 99% until confirm completes
          const percentage = Math.min(Math.round((loaded / total) * 100), 99);
          setUploadProgress({ loaded, total, percentage });
        },
      });

      // Step 3: confirm — Lambda reads from S3, validates PDF, writes DB record
      const result = user
        ? await documents.confirmUpload(document_id, file, folderIdRef.current)
        : await documents.confirmAnonymousUpload(document_id, file);

      setUploadProgress({ loaded: file.size, total: file.size, percentage: 100 });

      if (onUploadSuccess) {
        onUploadSuccess(result);
      }

      setTimeout(() => {
        setUploadProgress(null);
        setUploading(false);
      }, 1000);

    } catch (err: any) {
      const status: number | undefined = err.response?.status;
      const raw: string = err.response?.data?.message || err.message || 'Upload failed. Please try again.';
      const lower = raw.toLowerCase();

      let errorMessage: string;
      if (status === 413) {
        errorMessage = `File is too large. The maximum size is ${Math.round(getMaxFileSize() / (1024 * 1024))} MB.`;
      } else if (lower.includes('size exceeds') || lower.includes('file too large')) {
        errorMessage = !user ? `${raw} Create a free account to upload larger files.` : raw;
      } else if (lower.includes('limit reached') || lower.includes('can have at most')) {
        errorMessage = `${raw} Delete some documents to free up space.`;
      } else if (!err.response) {
        errorMessage = 'Upload failed — please check your connection and try again.';
      } else {
        errorMessage = raw;
      }

      setError(errorMessage);
      if (onUploadError) onUploadError(errorMessage);
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors.some((e: any) => e.code === 'file-too-large')) {
        const maxFileSize = getMaxFileSize();
        const maxSizeMB = maxFileSize ? Math.round(maxFileSize / (1024 * 1024)) : 'unlimited';
        setError(`File is too large. Maximum size is ${maxSizeMB}MB${!user ? ' for anonymous uploads' : ''}`);
      } else if (rejection.errors.some((e: any) => e.code === 'file-invalid-type')) {
        setError('File type not supported. Please upload a PDF file.');
      } else {
        setError('File upload failed. Please try again.');
      }
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;

    // Additional validation
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Start upload
    uploadFile(file);
  }, [user, folderId, maxSize]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    maxSize: getMaxFileSize(),
    disabled: uploading,
  });

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const maxFileSize = getMaxFileSize();
  const maxSizeText = maxFileSize ? formatFileSize(maxFileSize) : 'No limit';

  if (compact) {
    return (
      <div className={`file-uploader ${className}`}>
        {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
        <div
          {...getRootProps()}
          className={`upload-dropzone compact ${isDragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''}`}
        >
          <input {...getInputProps()} />
          <span className="compact-icon">{uploading ? '⏳' : isDragActive ? '📥' : '+'}</span>
          {uploading ? (
            <div className="compact-uploading">
              <span className="compact-label">Uploading…</span>
              {showProgress && uploadProgress && (
                <div className="compact-progress">
                  <div className="progress">
                    <div className="progress-bar" style={{ width: `${uploadProgress.percentage}%` }} />
                  </div>
                  <span className="progress-text">{uploadProgress.percentage}%</span>
                </div>
              )}
            </div>
          ) : isDragActive ? (
            <span className="compact-label">Drop file here</span>
          ) : (
            <span className="compact-label">Drop a file or <strong>click to browse</strong></span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`file-uploader ${className}`}>
      {error && (
        <ErrorMessage
          message={error}
          onDismiss={() => setError('')}
        />
      )}

      <div
        {...getRootProps()}
        className={`upload-dropzone ${isDragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''}`}
      >
        <input {...getInputProps()} />

        <div className="dropzone-icon">
          {uploading ? '⏳' : isDragActive ? '📥' : '📄'}
        </div>

        {uploading ? (
          <>
            <p className="dropzone-title">Uploading your document…</p>
            <LoadingSpinner size="small" message="" />
            {showProgress && uploadProgress && (
              <div className="upload-progress">
                <div className="progress">
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${uploadProgress.percentage}%` }}
                    aria-valuenow={uploadProgress.percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                <p className="progress-text">
                  {formatFileSize(uploadProgress.loaded)} / {formatFileSize(uploadProgress.total)} — {uploadProgress.percentage}%
                </p>
              </div>
            )}
          </>
        ) : isDragActive ? (
          <p className="dropzone-title">Drop your file here</p>
        ) : (
          <>
            <p className="dropzone-title">Drag &amp; drop a file, or click to browse</p>
            <p className="dropzone-hint">PDF only · Max size: {maxSizeText}</p>
            {!user && (
              <p className="dropzone-limit">
                <a href="/register">Create an account</a> for larger file uploads
              </p>
            )}
          </>
        )}
      </div>

      <div className="file-uploader-meta">
        <strong>Format:</strong> PDF &nbsp;·&nbsp;
        <strong>Limit:</strong> {maxSizeText}
        {!user && <>&nbsp;·&nbsp;<strong>Retention:</strong> expires after 7 days</>}
      </div>
    </div>
  );
};

export default FileUploader;